<template>
  <div class="pricing-page">
    <!-- PAGE HEADER -->
    <page-header
      title="Everything you need for your creative projects."
      subtitle="Images, animations, and layered Photoshop files."
      bgImage="/img/headers/pricing-header.webp"
    />

    <!-- Platforms -->
    <platforms />

    <!-- IMAGES -->
    <section class="bg-darker py-6">
      <div class="container">
        <div class="row">
          <div
            class="col-md-6 d-flex align-items-center custom-pricing-column order-md-2"
          >
            <div class="text-center inverted-text">
              <section-header title="IMAGES" inverted />
              <p class="my-4">
                Need cover art? Artwork for posters or social media posts?
                Browse Art Grab's massive selection of original artwork created
                by amazing artists from all over the world.
              </p>
            </div>
          </div>
          <div class="col-md-6 custom-pricing-column order-md-1">
            <div class="card custom-pricing-card text-center">
              <div class="custom-pricing-card-header bg-gradient-danger">
                <div class="title">
                  <i class="fas fa-image" />
                </div>
              </div>
              <div class="details">
                <p>
                  High-resolution images. A variety of sizes. Perfect for
                  digital use and printing.
                </p>
                <div class="w-100 details-text text-center">
                  <strong>
                    JPG and PNG
                    <br />Exclusive License
                  </strong>
                </div>
                <div class="buy-button">
                  <router-link
                    to="/store?type=image"
                    class="btn btn-primary btn-lg"
                    >SHOP IMAGES</router-link
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ANIMATIONS -->
    <section class="animation-section py-6">
      <div class="container">
        <div class="row">
          <div class="col-md-6 d-flex align-items-center custom-pricing-column">
            <div class="text-center">
              <section-header title="ANIMATIONS" inverted />
              <p class="my-4 inverted-text">
                Grab a short looping animation and use it for Spotify videos,
                Instagram stories, or TikTok.
              </p>
            </div>
          </div>
          <div class="col-md-6 custom-pricing-column">
            <div class="card custom-pricing-card text-center">
              <div class="custom-pricing-card-header bg-gradient-info">
                <div class="title">
                  <i class="fas fa-play-circle" />
                </div>
              </div>
              <div class="details">
                <p>
                  Short (3-8 seconds) looping animations. Portait sized (1920px
                  x 1080px).
                </p>
                <div class="w-100 details-text text-center">
                  <strong>
                    MP4 Video
                    <br />Exclusive License
                  </strong>
                </div>
                <div class="buy-button">
                  <router-link
                    to="/store?type=animation"
                    class="btn btn-primary btn-lg"
                    >SHOP ANIMATIONS</router-link
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PRO FILES -->
    <section class="bg-darker py-6">
      <div class="container">
        <div class="row">
          <div
            class="col-md-6 d-flex align-items-center custom-pricing-column order-md-2"
          >
            <div class="text-center inverted-text">
              <section-header title="PRO FILES" inverted />
              <p class="my-4">
                Need to edit, crop, manipulate, or animate your Art Grab
                artwork? PRO files are for you. Download a high-resolution
                Photoshop file (.psd) from our growing selection of PRO files.
              </p>
            </div>
          </div>
          <div class="col-md-6 custom-pricing-column order-md-1">
            <div class="card custom-pricing-card text-center">
              <div class="custom-pricing-card-header bg-gradient-success">
                <div class="title">
                  <img
                    src="/img/icons/common/pro-file-icon.png"
                    class="pro-file-icon"
                    alt="Pro file icon"
                  />
                </div>
              </div>
              <div class="details">
                <p>
                  High-resolution. Layered Photoshop files. Ready for editing or
                  animating.
                </p>
                <div class="w-100 details-text text-center">
                  <strong>
                    Photoshop (.psd)
                    <br />Exclusive License
                  </strong>
                </div>
                <div class="buy-button">
                  <router-link
                    to="/store?type=pro"
                    class="btn btn-primary btn-lg"
                    >SHOP PRO FILES</router-link
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <how-it-works backgroundColor="#f6f9fc" />

    <!-- FAQ CTA -->
    <call-to-action-section
      text="Have questions about how you can use licensed artwork? Check out our FAQ."
    >
      <router-link to="/about" class="btn btn-secondary"
        >FREQUENTLY ASKED QUESTIONS</router-link
      >
    </call-to-action-section>

    <!-- NEWSLETTER SIGNUP -->
    <newsletter-signup bg-image="/img/headers/newsletter-bg-2.jpg" />
  </div>
</template>
<script>
import {
  CallToActionSection,
  HowItWorks,
  NewsletterSignup,
  PageHeader,
  SectionHeader,
  Platforms,
} from "../../components";
const artgrabURL = process.env.VUE_APP_ARTGRAB_URL;

export default {
  name: "pricing-page",
  metaInfo() {
    return {
      title: "Art Grab : Products",
      meta: [
        {
          vmid: "twitter:card",
          name: "twitter:card",
          content: `summary_large_image`,
        },
        {
          vmid: "twitter:title",
          name: "twitter:title",
          content: `Art Grab : Products`,
        },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          content: `Everything you need for your creative projects. Images, animations, and layered Photoshop files.`,
        },
        {
          vmid: "twitter:image",
          name: "twitter:image",
          content: "https://artgrab.co/img/headers/pricing-header.webp",
        },
        {
          vmid: "og:url",
          property: "og:url",
          content: `https://artgrab.co/products`,
        },
        {
          vmid: "og:title",
          property: "og:title",
          content: `Art Grab : Products`,
        },
        {
          vmid: "og:description",
          property: "og:description",
          content: `Everything you need for your creative projects. Images, animations, and layered Photoshop files.`,
        },
        {
          vmid: "og:image",
          property: "og:image",
          content: "https://artgrab.co/img/headers/pricing-header.webp",
        },
        {
          vmid: "description",
          property: "description",
          content: `Everything you need for your creative projects. Images, animations, and layered Photoshop files.`,
        },
      ],
      link: [{ rel: "canonical", href: `${artgrabURL}/products` }],
    };
  },
  components: {
    CallToActionSection,
    HowItWorks,
    NewsletterSignup,
    PageHeader,
    SectionHeader,
    Platforms,
  },
  mounted: function () {
    this.$store.commit("setLayout", "page-layout");
  },
};
</script>
<style scoped lang="scss">
.pricing-page {
  hr {
    margin: 4.5rem 0;
  }

  .animation-section {
    background-image: url("/img/headers/animation-header.jpg");
    background-size: cover;
    background-position: center center;
  }

  .pricing-card-container {
    margin-top: -90px;
  }

  .save-badge {
    padding: 4px 14px;
    background: #2dce89;
    color: white;
    font-weight: 600;
    border-radius: 25px;
    position: absolute;
    z-index: 10;
    top: -16px;
    right: 28px;

    &.twenty-off {
      background: #11cdef;
    }
  }
}

@media (max-width: 991.98px) {
  .pricing-page {
    hr {
      margin: 1rem 0 3rem;
    }
  }
}

@media (max-width: 575.98px) {
  .pricing-page {
    hr {
      margin: 3rem 0;
    }

    .pricing-card-container {
      margin-top: -60px;
    }
  }
}

.custom-pricing-column {
  padding: 0 40px;

  .inverted-text {
    color: white;
  }
}

.custom-pricing-card {
  padding: 0;
  overflow: hidden;
  border: 1px solid #ececec;
  display: flex;
  flex-direction: column;
  margin: 0;

  .custom-pricing-card-header {
    // background: black;
    color: white;
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 205px;
    padding: 2rem 0.5rem;

    .title {
      i {
        font-size: 6rem;
      }

      .pro-file-icon {
        height: 96px;
      }

      .title-text {
        font-size: 1.125rem;
        font-weight: 600;
        margin-top: 0.5rem;
      }
    }

    .price {
      .price-text {
        font-size: 2rem;
        font-weight: 600;
        margin-top: 1rem;
        line-height: 1;
      }
    }
  }
  .details {
    padding: 2rem;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;

    p {
      margin-bottom: 0;
    }

    .details-text {
      margin: 1.5rem 0;
    }

    .detail-subheading {
      flex: 1;
      margin-bottom: 1rem;
    }
  }
}

@media (max-width: 575.98px) {
  .custom-pricing-card {
    flex-direction: column;

    .custom-pricing-card-header {
      width: 100%;
      flex-direction: row;
      justify-content: space-evenly;
      padding: 2.5rem 1rem;
      min-height: 140px;

      .title {
        .title-text {
          line-height: 1;
        }
      }

      .price {
        .price-text {
          margin-top: 0;
        }
      }
    }

    .details {
      p,
      .details-text {
        padding: 0.5rem 1.5rem;
      }

      .details-text {
        font-size: 0.875rem;
      }
    }
  }

  .custom-pricing-column {
    padding: 0 30px;
  }
}
</style>
